import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { take } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Modal } from '@spirit-testing/component-library';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { CodeIdChipComponent } from '../../components/code-id-chip/code-id-chip.component';
import { FileDropzoneComponent } from '../../components/file-dropzone/file-dropzone.component';
import { IconHintComponent } from '../../components/icon-hint/icon-hint.component';
import { ImageLightboxComponent } from '../../components/image-lightbox/image-lightbox.component';
import { LightboxController } from '../../components/image-lightbox/lightbox-controller';
import { ResPillComponent } from '../../components/res-pill/res-pill.component';
import { StepDotComponent } from '../../components/step-dot/step-dot.component';
import { BugTargetPickerComponent } from '../../components/bug-target-picker/bug-target-picker.component';
import { DefectErfassenDialogComponent } from '../../components/defect-erfassen-dialog/defect-erfassen-dialog.component';
import { Defect } from '../../models/defect.model';
import { canCaptureDefect, withDefect } from '../../components/defect-erfassen-dialog/defect-capture';
import {
  BugTargetChoice,
  NO_BUG_TARGET_CHOICE,
  resolveBugTargets,
} from '../../components/bug-target-picker/bug-target-choice';
import {
  ResultStatus,
  RunnerCase,
  RunnerDataSheet,
  RunnerOrderElement,
  RunnerState,
  RunnerStep,
  TestStepComment,
  UpdateRunnerStepRequest,
} from '../../models/test-run.model';
import { IstStatus, IstStatusService } from '../../services/ist-status.service';
import { TestRunService } from '../../services/test-run.service';
import { StepBugLinkService } from '../../services/step-bug-link.service';
import { ProjectRoleService } from '../../services/project-role.service';
import { StepReferenceImageService } from '../../services/step-reference-image.service';
import { TestlaufRunnerService } from '../../services/testlauf-runner.service';
import { runnerPillStatus, runnerResultKey } from './runner-status';
import { decidedSteps, hasOpenRatableStep, hasRatableSteps, isOpen, leafSteps } from './runner-steps';
import { resolveIterationValues } from './iteration-values';
import { RunnerPreconditionsComponent } from './runner-preconditions/runner-preconditions.component';
import { mapBugCreateError, TRACKER_ERROR_KEYS, TrackerError } from '../../shared/tracker-error';
import { FIELD_LENGTHS } from '../../shared/field-lengths';
import { NumberedStep, numberSteps } from './step-numbering';
import { StepMap } from './step-map';
import { RunnerDraftStore, StepDraft } from './runner-draft.store';

/**
 * Format eines Bug-Schluessels, gleichlautend mit der Pruefung am Server
 * ({@code UpdateStepResultRequest}). Das FE weist damit ab, was der Server ohnehin
 * mit 400 abwiese — und sagt es dort, wo getippt wurde.
 */
const BUG_KEY_PATTERN = /^[A-Z][A-Z0-9_]+-\d+$/;

/**
 * Eine Zeile der Schrittliste samt ihrer Symbole. Beide Kennzeichen werden hier
 * einmal je Aenderung berechnet statt im Template je Change-Detection-Zyklus — den
 * Umweg hat {@link TestlaufRunnerManuellComponent#orderRows} schon einmal ausgebaut.
 */
interface StepRow {
  entry: NumberedStep;
  /** Der Schritt traegt eine Vorlage aus der Bibliothek. */
  hasReference: boolean;
  /** Am Schritt haengt ein hochgeladener Nachweis. */
  hasEvidence: boolean;
}

interface OrderRow {
  element: RunnerOrderElement;
  /** Der Fall hinter dem Element — {@code null}, wenn der Lauf ihn nicht (mehr) traegt. */
  runnerCase: RunnerCase | null;
  isCurrent: boolean;
  /** Jeder auswertbare Schritt des Falls traegt ein Ergebnis. */
  isDone: boolean;
  /** Der Fall hat etwas zu bewerten ({@link hasRatableSteps}); nur dann springt der Runner ihn an. */
  hasSteps: boolean;
  /** Dem Element ist ein Testdatenblatt zugeordnet. */
  hasDataSheet: boolean;
  /** Stabiler Namensteil der Testkennungen dieser Zeile. */
  ref: string | number;
}

/**
 * Bestandene Schritte nach derselben Regel wie der Server (`RunnerStateDTO.of`).
 * Welche Schritte ueberhaupt zaehlen, beantwortet {@link leafSteps}.
 */
function countPassedSteps(cases: RunnerCase[]): number {
  return leafSteps(cases).filter((step) => step.actualStatus === 'PASSED').length;
}

/**
 * Master-Detail-Runner eines manuellen Durchlaufs (NEX-1042).
 *
 * <p>Zwei Spalten wie im Design (`page-testlauf.jsx:510`): links die
 * Ausfuehrungsreihenfolge, unter dem aktiven Element seine Schritte; rechts der
 * Schritt selbst mit Soll, Ergebnis, Ist, Nachweisen und Kommentaren.
 *
 * <p>Ist-Ergebnis und Notiz bleiben Entwurf, bis gespeichert wird — deshalb der
 * {@code CanDeactivate}-Guard der Seite. Sie liegen je Schritt getrennt vor, damit
 * ein Wechsel in der Liste nichts wegwirft.
 *
 * <p>Die Gruppenzeile traegt den Status ihres Moduls (NEX-1325). Dafuer gibt es keinen
 * Entwurf — das Design zeigt den Punkt nur an Schrittzeilen (Bundle v2026-10-02-bd42786a4dd, `page-testlauf.jsx:589`);
 * die Gruppenzeile nimmt deshalb denselben {@code app-step-dot}.
 */
@Component({
  selector: 'app-testlauf-runner-manuell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    NgTemplateOutlet,
    MatIconModule,
    TranslatePipe,
    AvatarComponent,
    BugTargetPickerComponent,
    DefectErfassenDialogComponent,
    CodeIdChipComponent,
    FileDropzoneComponent,
    IconHintComponent,
    ImageLightboxComponent,
    Modal,
    ResPillComponent,
    RunnerPreconditionsComponent,
    StepDotComponent,
  ],
  providers: [StepReferenceImageService, StepBugLinkService],
  templateUrl: './testlauf-runner-manuell.component.html',
  styleUrl: './testlauf-runner-manuell.component.scss',
})
export class TestlaufRunnerManuellComponent {
  private readonly runnerService = inject(TestlaufRunnerService);
  private readonly runService = inject(TestRunService);
  private readonly referenceImages = inject(StepReferenceImageService);
  private readonly bugLinks = inject(StepBugLinkService);
  private readonly projectRoleService = inject(ProjectRoleService);
  private readonly istStatusService = inject(IstStatusService);
  private readonly translate = inject(TranslateService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  readonly projectId = input.required<string>();
  readonly runId = input.required<string>();
  readonly state = input.required<RunnerState>();

  /** Der Server-Zustand ist veraltet (409) — die Seite laedt ihn neu. */
  readonly staleState = output<void>();

  /**
   * Der lokal fortgeschriebene Zustand. Die Seite zeigt Fortschritt und Ergebnis
   * ueber allen Varianten; ohne diese Meldung stuende ihr Kopf waehrend der ganzen
   * Arbeit auf dem Stand des ersten Ladens.
   */
  readonly stateChanged = output<RunnerState>();

  /**
   * Ungespeicherte Eingaben liegen hier, gebraucht werden sie am Router-Guard der
   * Seite. Gemeldet statt dort ausgelesen: haengt die Seite am {@code viewChild}
   * dieser Klasse, verliert ihr Guard fuer jede weitere Variante stillschweigend
   * seine Wirkung, weil der Zugriff dann leer bleibt.
   */
  readonly unsavedInput = output<boolean>();

  /** Ein Abschluss-Aufruf der Seite laeuft; solange bleibt der Knopf gesperrt. */
  readonly completing = input(false);

  /**
   * Jeder neue Wert fuehrt zum ersten fehlgeschlagenen Schritt ohne Ist. Ein Zaehler
   * statt eines Schalters, damit dieselbe Absage des Servers ein zweites Mal wirkt.
   */
  readonly revealMissingActual = input(0);

  /**
   * Der Abschluss des Durchlaufs, ausgeloest am letzten Schritt. Der Knopf steht im
   * Fuss des Schritt-Details, wie im Entwurf (page-testlauf.jsx:849); ausgefuehrt
   * wird er von der Seite, denn der Durchlauf ist ihr Gegenstand und nicht der
   * dieser Variante.
   */
  readonly completeRequested = output<void>();

  /**
   * Die waehlbaren Ist-Ergebnisse. Einmal je Runner gelesen: die Liste haengt an
   * der Projektkonfiguration, nicht am Schritt, und ein Neulesen je
   * Change-Detection-Zyklus brachte nur denselben Wert zurueck.
   */
  readonly results: readonly IstStatus[] = this.istStatusService.statuses();
  readonly fieldLengths = FIELD_LENGTHS;

  /** Nach dem Hochladen steht der Nachweis am Schritt — die Auswahl ist erledigt. */
  private readonly dropzone = viewChild(FileDropzoneComponent);
  private readonly actualField = viewChild<ElementRef<HTMLTextAreaElement>>('actualField');

  readonly selectedCaseId = signal<string | null>(null);
  readonly selectedStepId = signal<string | null>(null);
  readonly saving = signal(false);
  readonly uploading = signal(false);

  /** Ergebnis-Klicks, die auf das Ende der laufenden Schreibung warten, in der Reihenfolge der Klicks. */
  private readonly afterSaving: (() => void)[] = [];

  /**
   * Der Abschluss wartet getrennt von den Klicks und hoechstens einmal: eine zweite
   * Bestaetigung waehrend des Speicherns geht in ihm auf, statt einen zweiten anzuhaengen.
   */
  private pendingComplete: Map<string, StepDraft> | null = null;

  /** Die Vorlage formatfuellend, samt Fokusrueckgabe an das Bild, das sie geoeffnet hat. */
  readonly lightbox = new LightboxController();

  /**
   * Der Bug-Vorgang eines gescheiterten Schritts (NEX-1191). Je Schritt getrennt, aus
   * demselben Grund wie die uebrigen Entwuerfe: ein Wechsel in der Liste darf nichts
   * mitnehmen und nichts am falschen Schritt abschicken.
   */
  private readonly bugKeyDrafts = new StepMap<string>();
  /** Schritte, deren Pille gerade wieder zur Eingabe geoeffnet ist („aendern"). */
  private readonly bugKeyEditing = new StepMap<boolean>();
  private readonly creatingBugStepId = signal<string | null>(null);

  private readonly drafts = new RunnerDraftStore();

  /**
   * Der Zustand der Seite, lokal fortgeschrieben. Eine Antwort des Servers auf
   * einen einzelnen Schritt aktualisiert nur diesen Schritt — die ganze Seite
   * dafuer neu zu laden waere ein Sprung im Runner, in dem gerade jemand
   * arbeitet. Laedt die Seite neu, gewinnt wieder der Server.
   */
  private readonly localState = linkedSignal(() => this.state());

  readonly orderElements = computed<RunnerOrderElement[]>(() => this.localState().order);

  /**
   * Element, sein Fall und ob es das aktive ist — einmal gepaart. Im Template
   * standen dafuer drei Methodenaufrufe je Zeile, von denen jeder linear ueber die
   * Faelle suchte; Methodenaufrufe im Template laufen bei jedem
   * Change-Detection-Zyklus erneut.
   */
  readonly orderRows = computed<OrderRow[]>(() => {
    const all = this.cases();
    const activeId = this.activeCase()?.id ?? null;
    return this.orderElements().map((element) => {
      const runnerCase = all.find((entry) => entry.testCaseId === element.refId) ?? null;
      const steps = runnerCase ? leafSteps([runnerCase]) : [];
      return {
        element,
        runnerCase,
        isCurrent: runnerCase !== null && runnerCase.id === activeId,
        // Ein Element ohne Schritte ist nicht erledigt, sondern leer — `every` allein
        // haette es als fertig gemeldet.
        isDone: steps.length > 0 && steps.every((step) => !isOpen(step.actualStatus)),
        hasSteps: runnerCase !== null && hasRatableSteps(runnerCase),
        hasDataSheet: this.dataSheetFor(runnerCase) !== null,
        ref: element.refId ?? element.orderIndex,
      };
    });
  });

  /**
   * Die Faelle in Reihenfolge. Ohne Reihenfolge-Elemente (Altbestand, Lauf ohne
   * Quellverweise) tragen die Fall-Ergebnisse selbst die Reihenfolge — dieselbe
   * Ausweichlogik wie im Design.
   */
  readonly cases = computed<RunnerCase[]>(() => this.localState().cases);

  /** Die Schritte aller Faelle in Ausfuehrungsreihenfolge, Modulzeilen eingeschlossen. */
  private readonly allSteps = computed<RunnerStep[]>(() => this.cases().flatMap((runnerCase) => runnerCase.steps));

  /** Ohne Auswahl startet der Runner im ersten Fall mit Schritten (NEX-1673), sonst im ersten Fall. */
  readonly activeCase = computed<RunnerCase | null>(() => {
    const all = this.cases();
    if (all.length === 0) return null;
    return (
      all.find((runnerCase) => runnerCase.id === this.selectedCaseId()) ??
      all.find((runnerCase) => hasRatableSteps(runnerCase)) ??
      all[0]
    );
  });

  readonly activePreconditions = computed(() => this.activeCase()?.preconditions?.trim() ?? '');

  /** Je Testfall neu eingeklappt; ein gespeicherter Schritt laesst den Fall stehen und den Block offen. */
  readonly preconditionsOpen = linkedSignal<string | null, boolean>({
    source: () => this.activeCase()?.id ?? null,
    computation: () => false,
  });

  readonly steps = computed<NumberedStep[]>(() => numberSteps(resolveIterationValues(this.activeCase()?.steps ?? [])));

  readonly selectableSteps = computed<NumberedStep[]>(() => this.steps().filter((entry) => !entry.isGroup));

  readonly activeStep = computed<NumberedStep | null>(() => {
    const selectable = this.selectableSteps();
    if (selectable.length === 0) return null;
    const selected = selectable.find((entry) => entry.step.id === this.selectedStepId());
    if (selected) return selected;
    return selectable.find((entry) => isOpen(entry.step.actualStatus)) ?? selectable[0];
  });

  readonly activeTitle = computed(() => this.activeStep()?.step.title?.trim() ?? '');

  /** Ohne Aktion steht der Titel serverseitig als Ersatz in der Beschreibung — er soll nur einmal erscheinen. */
  readonly showAction = computed(() => {
    const title = this.activeTitle();
    return !title || (this.activeStep()?.step.description ?? '').trim() !== title;
  });

  readonly detailLabelledBy = computed(() =>
    [this.activeTitle() ? 'trm-detail-step-title' : '', this.showAction() ? 'trm-detail-title' : '']
      .filter(Boolean)
      .join(' '),
  );

  /**
   * Das Blatt, das ausdruecklich diesem Fall gilt. Verglichen wird ueber den
   * Schluessel des Falls; ohne Schluessel gibt es nichts zu vergleichen — ein Fall
   * ohne Schluessel und ein Blatt ohne Ziel waeren sonst „gleich" und das Blatt
   * landete am falschen Fall.
   */
  private dataSheetFor(runnerCase: RunnerCase | null): RunnerDataSheet | null {
    const key = runnerCase?.key;
    if (!key) return null;
    return this.localState().dataSheets.find((sheet) => sheet.target === key) ?? null;
  }

  /**
   * Die Gruppe des angezeigten Schritts (`page-testlauf.jsx:622`), leer wenn er zu
   * keiner gehoert. Der Gruppen-Eintrag ist sein Elternknoten in derselben Liste —
   * dieselbe Verbindung, aus der {@link numberSteps} den Einzug ableitet.
   */
  readonly activeGroupTitle = computed<string>(() => {
    const parentId = this.activeStep()?.step.parentStepResultId;
    if (!parentId) return '';
    const parent = this.steps().find((entry) => entry.step.id === parentId);
    return parent?.isGroup ? (parent.step.description ?? '') : '';
  });

  /**
   * Das Blatt neben dem angezeigten Schritt. Ohne Treffer faellt es auf das erste
   * zurueck, damit ein Lauf mit einem einzigen, nicht zugeordneten Blatt es
   * ueberhaupt zeigt. Die Reihenfolge-Liste uebernimmt diesen Notnagel nicht: dort
   * truege sonst jedes Element das Symbol und unterschiede nichts mehr.
   */
  readonly activeDataSheet = computed<RunnerDataSheet | null>(() => {
    const sheets = this.localState().dataSheets;
    if (sheets.length === 0) return null;
    return this.dataSheetFor(this.activeCase()) ?? sheets[0];
  });

  readonly dataSheetRowsKey = computed(() =>
    this.activeDataSheet()?.rows.length === 1 ? 'testRunner.dataSheetRowsOne' : 'testRunner.dataSheetRowsOther',
  );

  readonly draft = computed<StepDraft>(() => {
    const step = this.activeStep()?.step;
    return step ? this.drafts.draftOf(step) : { actualResult: '', note: '' };
  });

  readonly commentDraft = computed<string>(() => {
    const step = this.activeStep()?.step;
    return step ? this.drafts.commentOf(step.id) : '';
  });

  /** Mindestens ein Schritt traegt ungespeicherte Eingaben. */
  readonly dirty = this.drafts.dirty;

  /**
   * Ob der angezeigte Schritt ungespeicherte Eingaben traegt. Der Speichern-Knopf
   * haengt daran und nicht an {@link dirty}: der gilt ueber alle Schritte, und ein
   * Klick darauf schrieb sonst den unangetasteten Schritt fort, den man gerade
   * ansieht, weil an einem anderen ein Entwurf offen war.
   */
  readonly activeDirty = computed(() => {
    const step = this.activeStep()?.step;
    return step !== undefined && this.drafts.pending(step.id) !== undefined;
  });

  readonly failNeedsActual = computed(() => {
    const step = this.activeStep()?.step;
    return step?.actualStatus === 'FAILED' && this.draft().actualResult.trim() === '';
  });

  /**
   * Der letzte Schritt des aktiven Elements traegt ein Ergebnis; erst dann geht es zum
   * naechsten Reihenfolge-Element (`page-testlauf.jsx:871`). Ein Element ohne Schritte
   * wartet auf nichts. Den Abschluss regelt {@link completeBlockedHint} laufweit.
   */
  readonly lastStepDone = computed(() => {
    const selectable = this.selectableSteps();
    if (selectable.length === 0) return true;
    return !isOpen(selectable[selectable.length - 1].step.actualStatus);
  });

  /**
   * Das Element, das in der Reihenfolge auf das aktive folgt — {@code null}, wenn
   * keines mehr kommt. Uebersprungen wird, was nichts zu bewerten hat: ein Element,
   * dessen Fall der Lauf nicht (mehr) traegt, und ein Fall ohne Schritte (NEX-1673).
   * Folgt keines mit Schritten, steht am Ende das Abschliessen.
   */
  readonly nextElementRow = computed<OrderRow | null>(() => {
    const rows = this.orderRows();
    const position = rows.findIndex((row) => row.isCurrent);
    if (position < 0) return null;
    return rows.slice(position + 1).find((row) => row.hasSteps) ?? null;
  });

  /** Die Kennung des naechsten Elements, ersatzweise sein Titel. */
  readonly nextElementLabel = computed<string>(() => {
    const element = this.nextElementRow()?.element;
    if (!element) return '';
    return element.key?.trim() || (element.title ?? '');
  });

  readonly canGoToNextElement = computed(() => this.nextElementRow() !== null && this.lastStepDone());

  /**
   * Der erste fehlgeschlagene Schritt des ganzen Laufs, dem das Ist-Ergebnis fehlt —
   * gespeichert wie als Entwurf. Dieselbe Regel weist der Server beim Abschluss ab;
   * Modulzeilen nimmt er aus, weil ihr Status abgeleitet ist und sie nie ein Ist tragen.
   */
  readonly failedStepMissingActual = computed<RunnerStep | null>(
    () =>
      this.allSteps().find(
        (step) =>
          step.kind !== 'MODULE' &&
          step.actualStatus === 'FAILED' &&
          this.drafts.draftOf(step).actualResult.trim() === '',
      ) ?? null,
  );

  /**
   * Warum der Abschluss gesperrt ist; {@code null}, solange nichts dagegen spricht.
   * Offen darf im ganzen Lauf nichts mehr sein, nicht nur am aktiven Element (NEX-1673).
   */
  readonly completeBlockedHint = computed<string | null>(() => {
    if (hasOpenRatableStep(this.cases())) return 'testRunner.completeHint';
    if (this.failedStepMissingActual()) return 'testRunner.completeHintMissingActual';
    return null;
  });

  /** Abgeschlossen wird erst, wenn jeder Schritt des Laufs ein Ergebnis traegt und kein Fehlschlag ohne Ist bleibt. */
  readonly canComplete = computed(() => this.completeBlockedHint() === null && !this.completing());

  /** Die Rueckfrage vor dem Abschliessen steht offen (NEX-1496). */
  readonly confirmCompleteOpen = signal(false);

  /**
   * Was die Rueckfrage zusammenfasst: wie viel bewertet ist und was davon nicht
   * bestanden hat. Bewertet zaehlt wie der Fortschritt im Kopf der Seite.
   */
  readonly completeSummary = computed(() => {
    const cases = this.localState().cases;
    const leaves = leafSteps(cases);
    return {
      decided: decidedSteps(cases).length,
      total: leaves.length,
      failed: leaves.filter((step) => step.actualStatus === 'FAILED').length,
      blocked: leaves.filter((step) => step.actualStatus === 'BLOCKED').length,
    };
  });

  /** Blob-URL der Vorlage des angezeigten Schritts, `null` solange es keine gibt. */
  readonly activeReferenceUrl = computed<string | null>(() =>
    this.referenceImages.url(this.activeStep()?.step.testStepId),
  );

  /**
   * Ob der Durchlauf ueberhaupt noch Eingaben annimmt. Ein abgeschlossener oder
   * abgebrochener Lauf nimmt nichts mehr an, eine lesende Rolle bekaeme ein 403 —
   * dieselbe Regel, nach der die Seite das Kommentieren freigibt.
   */
  readonly readOnly = computed(
    () =>
      this.projectRoleService.isProjectReader() ||
      this.localState().runStatus === 'COMPLETED' ||
      this.localState().runStatus === 'ABORTED',
  );

  /** Der Block haengt am Ergebnis: ein Bug-Vorgang gehoert zu einem gescheiterten Schritt. */
  readonly showBugBlock = computed(() => this.activeStep()?.step.actualStatus === 'FAILED');

  /** Der gespeicherte Schluessel des angezeigten Schritts, leer wenn keiner verknuepft ist. */
  readonly linkedBugKey = computed(() => this.activeStep()?.step.linkedBugKey?.trim() ?? '');

  readonly bugKeyDraft = computed(() => {
    const step = this.activeStep()?.step;
    if (!step) return '';
    return this.bugKeyDrafts.get(step.id) ?? '';
  });

  /**
   * Die Pille haengt am gespeicherten Schluessel, nie am laufenden Entwurf: sonst
   * klappte die Eingabe mitten im Tippen zu, sobald die erste Ziffer das Muster
   * erfuellt. „aendern" haelt sie voruebergehend offen.
   */
  readonly isBugLinked = computed(() => {
    const step = this.activeStep()?.step;
    return !!this.linkedBugKey() && !!step && !this.bugKeyEditing.get(step.id);
  });

  /** Ein nicht leerer Entwurf, der das Muster verfehlt. */
  readonly bugKeyFormatError = computed(() => {
    const value = this.bugKeyDraft().trim();
    return !!value && !BUG_KEY_PATTERN.test(value);
  });

  readonly canCaptureDefect = computed(() => {
    const step = this.activeStep()?.step;
    return !!step && canCaptureDefect(step.kind, step.actualStatus);
  });

  readonly activeDefects = computed(() => this.activeStep()?.step.defects ?? []);

  readonly creatingBug = computed(() => this.creatingBugStepId() === this.activeStep()?.step.id);

  readonly bugTargets = this.bugLinks.creationTargets;

  /**
   * Die Auswahl gehoert dem Durchlauf, nicht dem Schritt: wer sein Ziel einmal nennt,
   * nennt es nicht bei jedem weiteren Fehlschlag erneut.
   */
  readonly bugTargetChoice = signal<BugTargetChoice>(NO_BUG_TARGET_CHOICE);

  readonly bugTargetOptions = computed(() => resolveBugTargets(this.bugTargets(), this.bugTargetChoice()));

  /** Die Verbindung, in der der naechste Vorgang entsteht. */
  readonly bugTarget = computed(() => this.bugTargetOptions().target);

  /**
   * Ob die Anbindungen des Projekts ueberhaupt bekannt sind. Vor der Antwort und nach
   * einem gescheiterten Abruf sind sie es nicht — dann ist „keine Verbindung hinterlegt"
   * eine falsche Auskunft, und der Knopf bleibt: ohne Ziel-ID waehlt der Server selbst.
   */
  readonly bugTargetsKnown = computed(() => this.bugLinks.integrationsLoadState() === 'ready');

  /** Das Ziel des Absprungs — woher es stammt, entscheidet {@link StepBugLinkService}. */
  readonly linkedBugUrl = computed<string | null>(() => {
    const step = this.activeStep()?.step;
    return step ? this.bugLinks.urlFor(step.id, this.linkedBugKey(), step.linkedBugIntegrationId) : null;
  });

  /** Die Schrittliste mit ihren Symbolen — siehe {@link StepRow}. */
  readonly stepRows = computed<StepRow[]>(() =>
    this.steps().map((entry) => ({
      entry,
      hasReference: this.referenceImages.has(entry.step.testStepId),
      hasEvidence: entry.step.attachments.length > 0,
    })),
  );

  constructor() {
    effect(() => this.unsavedInput.emit(this.dirty()));
    // Ob es eine Vorlage gibt, braucht die ganze Liste fuer ihr Symbol; die Bytes
    // holt nur der Schritt, den auch jemand ansieht.
    effect(() => {
      for (const entry of this.selectableSteps()) {
        this.referenceImages.discover(this.projectId(), entry.step.testStepId);
      }
    });
    effect(() => this.referenceImages.load(this.projectId(), this.activeStep()?.step.testStepId));
    // Im Effect statt direkt im Konstruktor: `projectId` ist ein Pflicht-Input und
    // steht dort noch nicht. Ein Wechsel des Projekts laedt die Anbindung neu, was
    // genau richtig waere — vorkommen wird er in dieser Komponente nicht.
    effect(() => this.bugLinks.load(this.projectId()));
    effect(() => {
      if (this.revealMissingActual() > 0) untracked(() => this.showFailedStepMissingActual());
    });
  }

  private showFailedStepMissingActual(): void {
    const step = this.failedStepMissingActual();
    const owner = step && this.cases().find((runnerCase) => runnerCase.steps.includes(step));
    if (!step || !owner) return;
    this.selectedCaseId.set(owner.id);
    this.selectedStepId.set(step.id);
    afterNextRender(() => this.actualField()?.nativeElement.focus(), { injector: this.injector });
  }

  onBugKeyInput(value: string): void {
    const step = this.activeStep()?.step;
    if (step) this.bugKeyDrafts.set(step.id, value.toUpperCase());
  }

  /** „aendern": oeffnet die Eingabe mit dem gespeicherten Wert, ohne ihn anzutasten. */
  editBugKey(): void {
    const step = this.activeStep()?.step;
    if (!step || this.readOnly()) return;
    this.bugKeyDrafts.set(step.id, this.linkedBugKey());
    this.bugKeyEditing.set(step.id, true);
  }

  /**
   * Schreibt den Schluessel am Schritt fort. Ein leerer Wert loest die Verknuepfung,
   * ein Wert im falschen Format wird gar nicht erst geschickt, ein unveraenderter ist
   * ein Nichts-Tun. Der offene Entwurf faehrt mit: das Verlassen des Felds per Klick
   * auf „Schritt speichern" sperrt den Knopf, bevor sein Klick ankommt (NEX-1522).
   */
  saveBugKey(): void {
    const step = this.activeStep()?.step;
    if (!step || this.readOnly()) return;
    const value = this.bugKeyDraft().trim();
    if (value && !BUG_KEY_PATTERN.test(value)) return;
    if (value === this.linkedBugKey()) {
      this.stopEditingBugKey(step.id);
      return;
    }
    const request = this.withDraft(step, { actualStatus: step.actualStatus, linkedBugKey: value });
    this.writeStep(step, request, (updated) => {
      this.stopEditingBugKey(step.id);
      this.drafts.clearIfSaved(updated);
    });
  }

  /**
   * Legt den Vorgang im angebundenen Tracker an und verknuepft ihn. Der Schluessel und
   * der Deep-Link kommen vom Server; ohne Anbindung meldet er das selbst (NEX-1117).
   */
  createBug(): void {
    const step = this.activeStep()?.step;
    if (!step || this.readOnly() || this.creatingBugStepId()) return;
    this.creatingBugStepId.set(step.id);
    this.runService
      .createBugFromStepResult(this.projectId(), step.id, { integrationId: this.bugTarget()?.id })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (bug) => {
          this.creatingBugStepId.set(null);
          this.bugLinks.remember(step.id, bug.browseUrl);
          this.replaceStep(step.id, (current) => ({ ...current, linkedBugKey: bug.key }));
          this.stopEditingBugKey(step.id);
          this.staleState.emit();
        },
        error: (error: HttpErrorResponse) => {
          this.creatingBugStepId.set(null);
          const tracker = mapBugCreateError(error, this.bugTarget()?.boardUrl);
          if (tracker) this.reportTrackerError(tracker);
          else this.reportError(error, 'testRunner.bug.createError');
        },
      });
  }

  /**
   * Absagen des Trackers gehen vor dem 409 „Stand veraltet“: auch eine deaktivierte Verbindung
   * antwortet mit 409, ein Neuladen hilft dort nicht (NEX-1354). Nur ein abgelehntes Token
   * fuehrt weiter ins Konto.
   */
  private reportTrackerError(tracker: TrackerError): void {
    const ref = this.snackBar.open(
      this.translate.instant(tracker.key, tracker.params),
      tracker.accountLink ? this.translate.instant(TRACKER_ERROR_KEYS.toAccount) : undefined,
      { duration: 6000 },
    );
    if (tracker.accountLink) {
      ref
        .onAction()
        .pipe(take(1))
        .subscribe(() => this.router.navigate(['/account']));
    }
  }

  /** Der Schritt, fuer den der Erfassen-Dialog offen ist. */
  readonly defectStepId = signal<string | null>(null);

  captureDefect(): void {
    const step = this.activeStep()?.step;
    if (step) this.defectStepId.set(step.id);
  }

  onDefectCreated(defect: Defect): void {
    const stepId = this.defectStepId();
    this.defectStepId.set(null);
    if (stepId) this.replaceStep(stepId, (current) => ({ ...current, defects: withDefect(current.defects, defect) }));
  }

  private stopEditingBugKey(stepId: string): void {
    this.bugKeyEditing.remove(stepId);
    this.bugKeyDrafts.remove(stepId);
  }

  readonly pillStatus = runnerPillStatus;
  readonly resultKey = runnerResultKey;

  /** Beschriftung eines Ergebnis-Knopfs — eigener Zweig, eigene Schluessel. */
  setResultKey(status: ResultStatus): string {
    return 'testRunner.setResult.' + status;
  }

  togglePreconditions(): void {
    this.preconditionsOpen.update((open) => !open);
  }

  selectElement(row: OrderRow): void {
    if (!row.runnerCase) return;
    this.selectedCaseId.set(row.runnerCase.id);
    this.selectedStepId.set(null);
  }

  /**
   * Weiter zum naechsten Reihenfolge-Element. Derselbe Weg wie beim Klick in die
   * Liste, damit der erste offene Schritt des neuen Elements greift. Entwuerfe
   * bleiben dabei stehen — sie liegen je Schritt getrennt vor.
   */
  goToNextElement(): void {
    if (!this.canGoToNextElement()) return;
    const row = this.nextElementRow();
    if (row) this.selectElement(row);
  }

  /**
   * Gesperrt heisst hier `aria-disabled`, damit der Hinweis daneben erreichbar bleibt.
   * Abschliessen friert den Lauf ein; darum fragt der Knopf erst nach (NEX-1496).
   */
  requestComplete(): void {
    if (!this.canComplete()) return;
    this.confirmCompleteOpen.set(true);
  }

  confirmComplete(): void {
    this.confirmCompleteOpen.set(false);
    if (this.readOnly() || !this.canComplete()) return;
    this.continueComplete(new Map());
  }

  /**
   * Der Server prueft den Abschluss am gespeicherten Stand. Ein Entwurf, der nur hier
   * steht, ginge mit dem Abschluss verloren — oder liesse ihn an einem Ist scheitern,
   * das man laengst ausgefuellt sieht. Scheitert eine Speicherung, bleibt der Lauf offen.
   */
  private saveDraftsThenComplete(sent = new Map<string, StepDraft>()): void {
    // Nur Entwuerfe, die so noch nicht geschickt wurden: Speichert der Server einen Text
    // anders, bliebe der Entwurf stehen und die Kette liefe sonst ohne Ende.
    const unsent = this.allSteps()
      .filter((step) => {
        const draft = this.drafts.pending(step.id);
        return draft !== undefined && draft !== sent.get(step.id);
      })
      .map((step) => step.id);
    if (unsent.length === 0) {
      this.completeRequested.emit();
      return;
    }
    this.saveInTurn(unsent, sent, () => this.continueComplete(sent));
  }

  /**
   * Ein wartender Ergebnis-Klick laeuft vor dem Abschluss, auch wenn er nach der
   * Bestaetigung kam: danach nimmt der Server keine Schreibung mehr an. Was
   * waehrenddessen getippt wurde, faehrt in einer weiteren Runde mit.
   */
  private continueComplete(sent: Map<string, StepDraft>): void {
    if (!this.saving() && this.afterSaving.length === 0) {
      this.pendingComplete = null;
      this.saveDraftsThenComplete(sent);
      return;
    }
    this.pendingComplete = new Map([...(this.pendingComplete ?? []), ...sent]);
    this.runAfterSaving();
  }

  /** Der Reihe nach, weil nur eine Schreibung zugleich laufen darf. */
  private saveInTurn(stepIds: string[], sent: Map<string, StepDraft>, done: () => void): void {
    const [stepId, ...rest] = stepIds;
    if (stepId === undefined) {
      done();
      return;
    }
    const step = this.findStep(stepId);
    const draft = this.drafts.pending(stepId);
    if (!step || !draft) {
      this.saveInTurn(rest, sent, done);
      return;
    }
    sent.set(stepId, draft);
    this.sendStep(step, this.withDraft(step, { actualStatus: step.actualStatus }), (updated) => {
      this.drafts.clearIfSaved(updated);
      this.saveInTurn(rest, sent, done);
    });
  }

  cancelComplete(): void {
    this.confirmCompleteOpen.set(false);
  }

  selectStep(entry: NumberedStep): void {
    if (entry.isGroup) return;
    this.selectedStepId.set(entry.step.id);
  }

  goPrevious(): void {
    this.moveBy(-1);
  }

  goNext(): void {
    this.moveBy(1);
  }

  readonly isFirstStep = computed(() => this.stepPosition() === 0);
  readonly isLastStep = computed(() => this.stepPosition() === this.selectableSteps().length - 1);

  private stepPosition(): number {
    const active = this.activeStep();
    if (!active) return 0;
    return this.selectableSteps().findIndex((entry) => entry.step.id === active.step.id);
  }

  private moveBy(offset: number): void {
    const selectable = this.selectableSteps();
    const next = selectable[this.stepPosition() + offset];
    if (next) this.selectedStepId.set(next.step.id);
  }

  updateActualResult(value: string): void {
    this.patchDraft({ actualResult: value });
  }

  updateNote(value: string): void {
    this.patchDraft({ note: value });
  }

  private patchDraft(change: Partial<StepDraft>): void {
    const step = this.activeStep()?.step;
    if (!step) return;
    this.selectedStepId.set(step.id);
    this.drafts.patch(step, change);
  }

  /**
   * Ergebnis setzen speichert den Schritt samt seiner Entwuerfe in einem Zug. Ein
   * zweiter Klick auf dasselbe Ergebnis nimmt es zurueck (`page-testlauf.jsx:646`):
   * der Schritt steht danach wieder offen.
   */
  setResult(status: ResultStatus): void {
    const step = this.activeStep()?.step;
    if (!step || this.readOnly()) return;
    const next = step.actualStatus === status ? 'NOT_RUN' : status;
    if (!this.saving()) {
      this.writeStatus(step, next);
      return;
    }
    // Verlaesst man das Ist-Feld per Klick auf ein Ergebnis, laeuft dessen Speichern
    // schon, wenn der Klick ankommt. Gesperrte Knoepfe schluckten ihn. Der Entwurf wird
    // am Stand nach dem Speichern gemessen, sonst ginge das eben gespeicherte Ist erneut mit.
    this.afterSaving.push(() => {
      const current = this.findStep(step.id);
      if (current) this.writeStatus(current, next);
    });
  }

  private findStep(stepId: string): RunnerStep | undefined {
    return this.allSteps().find((candidate) => candidate.id === stepId);
  }

  saveDraft(): void {
    const step = this.activeStep()?.step;
    if (step) this.writeStatus(step, step.actualStatus);
  }

  /**
   * Das Ist-Ergebnis haelt fest, was beobachtet wurde; wer danach weiterklickt, rechnet
   * nicht damit, es noch eigens speichern zu muessen. Die Notiz faehrt mit, weil der
   * PATCH den ganzen Entwurf des Schritts schreibt.
   */
  saveActualOnLeave(): void {
    if (this.readOnly() || !this.activeDirty()) return;
    this.saveDraft();
  }

  private writeStatus(step: RunnerStep, status: ResultStatus): void {
    this.writeStep(step, this.withDraft(step, { actualStatus: status }), (updated) =>
      this.drafts.clearIfSaved(updated),
    );
  }

  private withDraft(step: RunnerStep, request: UpdateRunnerStepRequest): UpdateRunnerStepRequest {
    return { ...request, ...this.drafts.unsavedFields(step) };
  }

  /**
   * Der eine Weg, auf dem ein Schritt fortgeschrieben wird. Alles, was fuer jede
   * Schreibung gilt, steht hier: dass nur eine zugleich laeuft, dass die Auswahl auf
   * dem bearbeiteten Schritt stehen bleibt — ohne die Festlegung wanderte sie auf den
   * naechsten offenen, weil sie sonst aus dem Zustand abgeleitet wird, und der aendert
   * sich gerade — und wie ein Fehlschlag gemeldet wird. Der Aufrufer bringt nur die
   * Nutzlast mit und sagt, was nach dem Speichern aufzuraeumen ist.
   */
  private writeStep(step: RunnerStep, request: UpdateRunnerStepRequest, onSaved: (updated: RunnerStep) => void): void {
    if (this.saving()) return;
    this.selectedStepId.set(step.id);
    this.sendStep(step, request, onSaved);
  }

  /**
   * Die Schreibung selbst, ohne die Auswahl anzufassen: vor dem Abschluss werden auch
   * Schritte gespeichert, die gerade niemand ansieht.
   */
  private sendStep(step: RunnerStep, request: UpdateRunnerStepRequest, onSaved: (updated: RunnerStep) => void): void {
    this.saving.set(true);
    this.runnerService
      .updateStep(this.projectId(), this.runId(), this.localState().executionId, step.id, {
        ...request,
        expectedVersion: this.findStep(step.id)?.version ?? step.version,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.saving.set(false);
          this.applyStep(updated);
          onSaved(updated);
          this.runAfterSaving();
        },
        error: (error: HttpErrorResponse) => {
          this.saving.set(false);
          this.afterSaving.length = 0;
          this.pendingComplete = null;
          this.reportError(error, 'testRunner.saveError');
        },
      });
  }

  /**
   * Was waehrend einer Schreibung angestossen wurde, laeuft nach ihr, die Klicks vor dem
   * Abschluss. Setzt der Aufrufer in {@code onSaved} schon die naechste fort, wartet es
   * auch auf die.
   */
  private runAfterSaving(): void {
    while (!this.saving()) {
      const next = this.afterSaving.shift();
      if (next) {
        next();
        continue;
      }
      const sent = this.pendingComplete;
      if (!sent) return;
      this.pendingComplete = null;
      this.saveDraftsThenComplete(sent);
    }
  }

  updateComment(value: string): void {
    const step = this.activeStep()?.step;
    if (step) this.drafts.setComment(step.id, value);
  }

  addComment(): void {
    const step = this.activeStep()?.step;
    const body = this.commentDraft().trim();
    if (!step || body === '') return;
    this.runnerService
      .addComment(this.projectId(), this.runId(), this.localState().executionId, step.id, body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (comment) => {
          this.drafts.clearComment(step.id);
          this.appendComment(step.id, comment);
        },
        error: (error: HttpErrorResponse) => this.reportError(error, 'testRunner.commentError'),
      });
  }

  /**
   * Der hochgeladene Nachweis kommt an den Schritt, wie er beim Eintreffen der
   * Antwort steht — nicht an den Stand von vorher. Sonst faellt ein Ergebnis, das
   * waehrend des Uploads gespeichert wurde, samt Version wieder zurueck.
   */
  uploadEvidence(file: File): void {
    const step = this.activeStep()?.step;
    if (!step) return;
    this.uploading.set(true);
    this.runService
      .uploadStepResultAttachment(this.projectId(), step.id, file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (attachment) => {
          this.uploading.set(false);
          this.dropzone()?.clear();
          this.replaceStep(step.id, (current) => ({
            ...current,
            attachments: [attachment, ...current.attachments],
          }));
        },
        error: (error: HttpErrorResponse) => {
          this.uploading.set(false);
          this.dropzone()?.clear();
          this.reportError(error, 'testRunner.uploadError');
        },
      });
  }

  /**
   * Der Server ist die Wahrheit ueber den Schritt: seine Antwort ersetzt den
   * lokalen Stand, statt ihn zu ergaenzen. Nur so stimmen Version und Zeitstempel
   * nach dem Speichern.
   */
  private applyStep(updated: RunnerStep): void {
    this.replaceStep(updated.id, () => updated);
  }

  private appendComment(stepId: string, comment: TestStepComment): void {
    this.replaceStep(stepId, (step) => ({ ...step, comments: [...step.comments, comment] }));
  }

  private replaceStep(stepId: string, change: (step: RunnerStep) => RunnerStep): void {
    const current = this.localState();
    const cases = current.cases.map((runnerCase) => ({
      ...runnerCase,
      steps: runnerCase.steps.map((step) => (step.id === stepId ? change(step) : step)),
    }));
    const next: RunnerState = { ...current, cases, stepsPassed: countPassedSteps(cases) };
    this.localState.set(next);
    this.stateChanged.emit(next);
  }

  /**
   * Ein 409 heisst: der Durchlauf ist abgeschlossen, jemand war schneller oder der
   * Schritt wurde seit dem Laden anderswo gespeichert (`STALE_VERSION`). Dann hilft
   * kein erneuter Versuch, sondern nur ein frischer Zustand.
   */
  private reportError(error: HttpErrorResponse, messageKey: string): void {
    const stale = error.status === 409;
    const staleKey = error.error?.code === 'STALE_VERSION' ? 'testRunner.stepStaleError' : 'testRunner.staleError';
    this.snackBar.open(this.translate.instant(stale ? staleKey : messageKey), undefined, {
      duration: 5000,
    });
    if (stale) this.staleState.emit();
  }
}
